import { FetchedOrderUploadProduct } from "../types";
import { SortConfig } from "../types";
import { OrderUploadItemDetailsResponse } from "../../../interfaces/interfaces";

/**
 * Type guard to check if product is OrderUploadItemDetailsResponse
 */
const isOrderUploadItemDetailsResponse = (
  product: FetchedOrderUploadProduct,
): product is OrderUploadItemDetailsResponse => "product_id" in product;

/**
 * Finds the number of invalid products in the list
 * @param products - Array of products to validate
 * @returns Number of invalid products
 */
export const findNumberOfInvalidProducts = (products: FetchedOrderUploadProduct[]): number => {
  if (!Array.isArray(products)) {
    return 0;
  }

  const invalidProducts = products.filter(
    (product) => !product || !product.price || !product.itemNo,
  );

  return invalidProducts.length;
};

/**
 * Sorts products based on specified criteria
 * @param products - Array of products to sort
 * @param sort - Sort configuration
 * @returns Sorted array of products
 */
export const sortProducts = (
  products: FetchedOrderUploadProduct[],
  sort: SortConfig = { ascending: true, type: "index" },
): FetchedOrderUploadProduct[] => {
  if (!Array.isArray(products)) {
    return [];
  }

  if (sort.type === "none") {
    return [...products];
  }

  const sortedProducts = [...products];

  switch (sort.type) {
    case "index":
      sortedProducts.sort((a, b) => {
        const indexA = parseInt(a.index || "0", 10);
        const indexB = parseInt(b.index || "0", 10);
        return sort.ascending ? indexA - indexB : indexB - indexA;
      });
      break;

    case "stock":
      sortedProducts.sort((a, b) => {
        const totalStockA = calculateTotalStock(a);
        const totalStockB = calculateTotalStock(b);
        return sort.ascending ? totalStockA - totalStockB : totalStockB - totalStockA;
      });
      break;

    case "b/o":
      sortedProducts.sort((a, b) => {
        const backorderA = calculateBackorderQuantity(a);
        const backorderB = calculateBackorderQuantity(b);
        return sort.ascending ? backorderA - backorderB : backorderB - backorderA;
      });
      break;

    default:
      // No sorting
      break;
  }

  return sortedProducts;
};

/**
 * Calculates total stock for a product
 * @param product - Product to calculate stock for
 * @returns Total available stock
 */
const calculateTotalStock = (product: FetchedOrderUploadProduct): number => {
  if (
    !product ||
    !isOrderUploadItemDetailsResponse(product) ||
    !product.stock ||
    !Array.isArray(product.stock)
  ) {
    return 0;
  }

  return product.stock.reduce(
    (total: number, warehouse) => total + (warehouse.qtyAvailable || 0),
    0,
  );
};

/**
 * Calculates backorder quantity for a product
 * @param product - Product to calculate backorder for
 * @returns Total backorder quantity
 */
const calculateBackorderQuantity = (product: FetchedOrderUploadProduct): number => {
  if (!product || !isOrderUploadItemDetailsResponse(product) || !product.backorder_quantity) {
    return 0;
  }

  // backorder_quantity is a string, so we need to parse it
  const backorderQuantity = parseInt(product.backorder_quantity, 10);
  return isNaN(backorderQuantity) ? 0 : backorderQuantity;
};

/**
 * Validates if a product is complete and valid
 * @param product - Product to validate
 * @returns Validation result
 */
export const validateProduct = (
  product: FetchedOrderUploadProduct,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!product) {
    errors.push("Product is null or undefined");
    return { isValid: false, errors };
  }

  if (!product.index) {
    errors.push("Product index is missing");
  }

  // Check SKU only for OrderUploadItemDetailsResponse
  if (isOrderUploadItemDetailsResponse(product)) {
    if (!product.sku) {
      errors.push("Product SKU is missing");
    }

    if (!product.quantity || parseInt(product.quantity.toString(), 10) <= 0) {
      errors.push("Product quantity is invalid");
    }
  }

  if (!product.price) {
    errors.push("Product price is missing");
  }

  if (!product.itemNo) {
    errors.push("Product item number is missing");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Filters products by validity
 * @param products - Array of products to filter
 * @returns Object containing valid and invalid products
 */
export const filterProductsByValidity = (products: FetchedOrderUploadProduct[]) => {
  const valid: FetchedOrderUploadProduct[] = [];
  const invalid: FetchedOrderUploadProduct[] = [];

  products.forEach((product) => {
    const validation = validateProduct(product);
    if (validation.isValid) {
      valid.push(product);
    } else {
      invalid.push(product);
    }
  });

  return { valid, invalid };
};
